/**
 * Degrees Section Component (Student Portal)
 *
 * Displays student's enrolled degree programs in a structured 4-panel card format
 * identically aligned with the SIS admin interface (contacts-degrees-section),
 * strictly read-only for students.
 */

import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ContactDegreeDetail } from '../../core/models';
import { TranslatePipe } from '../../core/features/translations';

@Component({
  selector: 'app-degrees-section',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <div class="flex flex-col gap-4">
      @if (degrees().length > 0) {
        <div class="flex flex-col gap-4">
          @for (degree of degrees(); track degree.contact_degree_id || degree.degree_id) {
            <div class="bg-white rounded-xl border border-gray-200 shadow-sm hover:border-blue-300 transition-all p-5">
              <!-- Card Header -->
              <div class="flex flex-wrap items-center justify-between gap-4 pb-4 mb-4 border-b border-gray-100">
                <!-- Left: Title & Subtitle -->
                <div class="flex items-center gap-3 min-w-0 max-w-full sm:max-w-[45%]">
                  <div class="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <span class="material-symbols-outlined text-xl">school</span>
                  </div>
                  <div class="flex flex-col min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                      <h4 class="font-bold text-gray-900 text-base m-0 truncate" [title]="getDegreeTitle(degree)">
                        {{ getDegreeTitle(degree) }}
                      </h4>
                      @if (degree.is_main_degree) {
                        <span class="px-2 py-0.5 text-[0.7rem] font-bold uppercase rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                          {{ 'contacts.degrees.is_main_degree_label' | translate }}
                        </span>
                      }
                    </div>
                    <div class="flex items-center gap-2 text-xs text-gray-500 mt-0.5 flex-wrap">
                      @if (degree.school_name) {
                        <span>{{ degree.school_name }}</span>
                      }
                      @if (degree.department_name) {
                        <span>•</span>
                        <span>{{ degree.department_name }}</span>
                      }
                    </div>
                  </div>
                </div>

                <!-- Center: Attendance % Indicator -->
                <div class="flex flex-col items-center justify-center mx-auto sm:my-0 my-1">
                  @if (degree.total_lectures && degree.total_lectures > 0) {
                    <div class="flex items-center gap-2 px-3.5 py-1.5 rounded-xl border bg-gray-50/90 shadow-xs"
                      [class.border-emerald-200]="(degree.attendance_rate ?? 0) >= 80"
                      [class.bg-emerald-50/60]="(degree.attendance_rate ?? 0) >= 80"
                      [class.border-amber-200]="(degree.attendance_rate ?? 0) >= 70 && (degree.attendance_rate ?? 0) < 80"
                      [class.bg-amber-50/60]="(degree.attendance_rate ?? 0) >= 70 && (degree.attendance_rate ?? 0) < 80"
                      [class.border-rose-200]="(degree.attendance_rate ?? 0) < 70"
                      [class.bg-rose-50/60]="(degree.attendance_rate ?? 0) < 70"
                    >
                      <span class="material-symbols-outlined text-lg"
                        [class.text-emerald-600]="(degree.attendance_rate ?? 0) >= 80"
                        [class.text-amber-600]="(degree.attendance_rate ?? 0) >= 70 && (degree.attendance_rate ?? 0) < 80"
                        [class.text-rose-600]="(degree.attendance_rate ?? 0) < 70"
                      >
                        how_to_reg
                      </span>
                      <div class="flex flex-col items-start leading-none">
                        <span class="text-[0.65rem] font-bold uppercase tracking-wider text-gray-500">
                          {{ 'portal.degrees.attendance.title' | translate }}
                        </span>
                        <span class="text-sm font-extrabold"
                          [class.text-emerald-700]="(degree.attendance_rate ?? 0) >= 80"
                          [class.text-amber-700]="(degree.attendance_rate ?? 0) >= 70 && (degree.attendance_rate ?? 0) < 80"
                          [class.text-rose-700]="(degree.attendance_rate ?? 0) < 70"
                        >
                          {{ degree.attendance_rate !== null && degree.attendance_rate !== undefined ? degree.attendance_rate + '%' : '-' }}
                        </span>
                      </div>
                    </div>
                  } @else {
                    <div class="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-gray-50 border border-gray-200 text-gray-400 text-xs font-medium">
                      <span class="material-symbols-outlined text-sm">schedule</span>
                      <span>{{ 'portal.degrees.attendance.no_data' | translate }}</span>
                    </div>
                  }
                </div>

                <!-- Right: Status -->
                <div class="flex items-center gap-2">
                  <div
                    class="px-2.5 py-1 text-xs font-bold uppercase tracking-wider rounded-lg"
                    [class.bg-red-50]="isStatusCancelled(degree.enrollment_status_name)"
                    [class.text-red-700]="isStatusCancelled(degree.enrollment_status_name)"
                    [class.bg-green-50]="isStatusActive(degree.enrollment_status_name)"
                    [class.text-green-700]="isStatusActive(degree.enrollment_status_name)"
                    [class.bg-gray-100]="!isStatusActive(degree.enrollment_status_name) && !isStatusCancelled(degree.enrollment_status_name)"
                    [class.text-gray-700]="!isStatusActive(degree.enrollment_status_name) && !isStatusCancelled(degree.enrollment_status_name)"
                  >
                    {{ degree.enrollment_status_name || ('portal.degrees.degrees.table.status' | translate) }}
                  </div>
                </div>
              </div>

              <!-- Degree Attendance Summary Strip (Attended / Absent / Rate) -->
              @if (degree.total_lectures && degree.total_lectures > 0) {
                <div class="mb-4 p-3 rounded-xl bg-linear-to-r from-blue-50/50 via-gray-50/60 to-emerald-50/40 border border-gray-200/80 flex flex-wrap items-center justify-between gap-3 text-xs">
                  <div class="flex items-center gap-2 font-bold text-gray-700 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-base text-blue-600">donut_large</span>
                    <span>{{ 'portal.degrees.attendance.summary_title' | translate }}</span>
                  </div>

                  <div class="flex items-center gap-6 flex-wrap">
                    <!-- Attended -->
                    <div class="flex items-center gap-2">
                      <span class="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                      <span class="text-gray-500 font-medium">{{ 'portal.degrees.attendance.attended' | translate }}:</span>
                      <span class="font-bold text-gray-900 text-sm">{{ degree.attended_lectures || 0 }}</span>
                    </div>

                    <!-- Absent -->
                    <div class="flex items-center gap-2">
                      <span class="w-2.5 h-2.5 rounded-full bg-rose-500"></span>
                      <span class="text-gray-500 font-medium">{{ 'portal.degrees.attendance.absent' | translate }}:</span>
                      <span class="font-bold text-gray-900 text-sm">{{ degree.absent_lectures || 0 }}</span>
                    </div>

                    <!-- Rate -->
                    <div class="flex items-center gap-2">
                      <span class="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                      <span class="text-gray-500 font-medium">{{ 'portal.degrees.attendance.rate' | translate }}:</span>
                      <span class="font-extrabold text-sm"
                        [class.text-emerald-600]="(degree.attendance_rate ?? 0) >= 80"
                        [class.text-amber-600]="(degree.attendance_rate ?? 0) >= 70 && (degree.attendance_rate ?? 0) < 80"
                        [class.text-rose-600]="(degree.attendance_rate ?? 0) < 70"
                      >
                        {{ degree.attendance_rate !== null && degree.attendance_rate !== undefined ? degree.attendance_rate + '%' : '-' }}
                      </span>
                    </div>
                  </div>
                </div>
              }

              <!-- Card Body: 4 Read-Only Panels -->
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <!-- Panel 1: Allgemein & Zeitraum -->
                <div class="p-3.5 rounded-xl bg-gray-50/70 border border-gray-200/60 flex flex-col gap-2.5">
                  <span class="text-sm font-bold uppercase tracking-wider text-blue-600 flex items-center gap-2">
                    <span class="material-symbols-outlined text-base text-blue-600">calendar_today</span>
                    {{ 'contacts.degrees.general_period_title' | translate }}
                  </span>
                  <div class="flex flex-col gap-2 mt-0.5">
                    <div>
                      <span class="text-xs font-medium text-gray-500 block uppercase tracking-wider">{{ 'contacts.degrees.period_label' | translate }}:</span>
                      <span class="font-semibold text-gray-900 text-sm">{{ formatDate(degree.start_date) }} – {{ formatDate(degree.end_date) }}</span>
                    </div>
                    <div>
                      <span class="text-xs font-medium text-gray-500 block uppercase tracking-wider">{{ 'contacts.degrees.enrollment_date_label' | translate }}:</span>
                      <span class="font-semibold text-gray-900 text-sm">{{ formatDate(degree.enrollment_date) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Panel 2: Abschluss & Diplom -->
                <div class="p-3.5 rounded-xl bg-gray-50/70 border border-gray-200/60 flex flex-col gap-2.5">
                  <span class="text-sm font-bold uppercase tracking-wider text-blue-600 flex items-center gap-2">
                    <span class="material-symbols-outlined text-base text-blue-600">workspace_premium</span>
                    {{ 'contacts.degrees.graduation_diploma_title' | translate }}
                  </span>
                  <div class="flex flex-col gap-2 mt-0.5">
                    <div>
                      <span class="text-xs font-medium text-gray-500 block uppercase tracking-wider">{{ 'contacts.degrees.degree_grade_label' | translate }}:</span>
                      <span class="font-bold text-base" [class.text-blue-600]="degree.degree_grade" [class.text-gray-400]="!degree.degree_grade">
                        {{ degree.degree_grade || '-' }}
                      </span>
                    </div>
                    <div>
                      <span class="text-xs font-medium text-gray-500 block uppercase tracking-wider">{{ 'contacts.degrees.diploma_title_label' | translate }}:</span>
                      <span class="font-semibold text-gray-900 truncate block text-sm" [title]="degree.diploma_title || '-'">
                        {{ degree.diploma_title || '-' }}
                      </span>
                    </div>
                    <div>
                      <span class="text-xs font-medium text-gray-500 block uppercase tracking-wider">{{ 'contacts.degrees.diploma_date_label' | translate }}:</span>
                      <span class="font-semibold text-gray-900 text-sm">{{ formatDate(degree.diploma_date) }}</span>
                    </div>
                    <div>
                      <span class="text-xs font-medium text-gray-500 block uppercase tracking-wider">{{ 'contacts.degrees.diploma_institute_label' | translate }}:</span>
                      <span class="font-semibold text-gray-900 text-sm">{{ degree.diploma_institute || '-' }}</span>
                    </div>
                    <div>
                      <span class="text-xs font-medium text-gray-500 block uppercase tracking-wider">{{ 'contacts.degrees.registernumber_label' | translate }}:</span>
                      <span class="font-semibold text-gray-900 font-mono text-sm">{{ degree.registernumber || '-' }}</span>
                    </div>
                  </div>
                </div>

                <!-- Panel 3: Finanzen & Modalitäten -->
                <div class="p-3.5 rounded-xl bg-gray-50/70 border border-gray-200/60 flex flex-col gap-2.5">
                  <span class="text-sm font-bold uppercase tracking-wider text-blue-600 flex items-center gap-2">
                    <span class="material-symbols-outlined text-base text-blue-600">payments</span>
                    {{ 'contacts.degrees.financials_title' | translate }}
                  </span>
                  <div class="flex flex-col gap-2 mt-0.5">
                    <div>
                      <span class="text-xs font-medium text-gray-500 block uppercase tracking-wider">{{ 'contacts.degrees.discount_reason_label' | translate }}:</span>
                      <span class="font-semibold text-gray-900 text-sm">{{ degree.discount_reason || '-' }}</span>
                    </div>
                    <div>
                      <span class="text-xs font-medium text-gray-500 block uppercase tracking-wider">{{ 'contacts.degrees.invoice_address_label' | translate }}:</span>
                      <span class="font-semibold text-gray-900 text-sm">{{ degree.invoice_address ? ('ID: ' + degree.invoice_address) : '-' }}</span>
                    </div>
                    <div class="flex flex-col gap-1.5 pt-1">
                      <label class="inline-flex items-center gap-2 cursor-default select-none">
                        <input
                          type="checkbox"
                          [checked]="!!degree.registration_fee"
                          disabled
                          class="w-4 h-4 rounded text-blue-600 border-gray-300 focus:ring-0 opacity-80"
                        />
                        <span class="text-sm text-gray-800">{{ 'contacts.degrees.registration_fee_label' | translate }}</span>
                      </label>
                      <label class="inline-flex items-center gap-2 cursor-default select-none">
                        <input
                          type="checkbox"
                          [checked]="!!degree.invoice_50_50"
                          disabled
                          class="w-4 h-4 rounded text-blue-600 border-gray-300 focus:ring-0 opacity-80"
                        />
                        <span class="text-sm text-gray-800">{{ 'contacts.degrees.invoice_50_50_label' | translate }}</span>
                      </label>
                      <label class="inline-flex items-center gap-2 cursor-default select-none">
                        <input
                          type="checkbox"
                          [checked]="!!degree.partial_payment"
                          disabled
                          class="w-4 h-4 rounded text-blue-600 border-gray-300 focus:ring-0 opacity-80"
                        />
                        <span class="text-sm text-gray-800">{{ 'contacts.degrees.partial_payment_label' | translate }}</span>
                      </label>
                      <label class="inline-flex items-center gap-2 cursor-default select-none">
                        <input
                          type="checkbox"
                          [checked]="!!degree.invoice_full_upfront"
                          disabled
                          class="w-4 h-4 rounded text-blue-600 border-gray-300 focus:ring-0 opacity-80"
                        />
                        <span class="text-sm text-gray-800">{{ 'contacts.degrees.invoice_full_upfront_label' | translate }}</span>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Panel 4: Upgrade & Abbruch -->
                <div class="p-3.5 rounded-xl bg-gray-50/70 border border-gray-200/60 flex flex-col gap-2.5">
                  <span class="text-sm font-bold uppercase tracking-wider text-blue-600 flex items-center gap-2">
                    <span class="material-symbols-outlined text-base text-blue-600">swap_horiz</span>
                    {{ 'contacts.degrees.upgrade_status_title' | translate }}
                  </span>
                  <div class="flex flex-col gap-2 mt-0.5">
                    <div class="pt-0.5">
                      <label class="inline-flex items-center gap-2 cursor-default select-none">
                        <input
                          type="checkbox"
                          [checked]="!!degree.course_upgrade"
                          disabled
                          class="w-4 h-4 rounded text-blue-600 border-gray-300 focus:ring-0 opacity-80"
                        />
                        <span class="text-sm text-gray-800">{{ 'contacts.degrees.course_upgrade_label' | translate }}</span>
                      </label>
                      @if (degree.course_upgrade) {
                        <div class="mt-1 pl-6">
                          <span class="text-xs font-medium text-gray-500 block uppercase tracking-wider">{{ 'contacts.degrees.course_upgrade_start_label' | translate }}:</span>
                          <span class="font-semibold text-gray-900 text-sm">
                            {{ formatDate(degree.course_upgrade_start) }}
                            @if (degree.upgrade_degree_code) {
                              <span>({{ degree.upgrade_degree_code }})</span>
                            }
                          </span>
                        </div>
                      }
                    </div>
                    <div>
                      <span class="text-xs font-medium text-gray-500 block uppercase tracking-wider">{{ 'contacts.degrees.cancel_date_label' | translate }}:</span>
                      <span class="font-semibold text-gray-900 text-sm">
                        {{ formatDate(degree.cancel_date) }}
                      </span>
                    </div>
                    <div>
                      <span class="text-xs font-medium text-gray-500 block uppercase tracking-wider">{{ 'contacts.degrees.cancel_reason_label' | translate }}:</span>
                      <span class="font-semibold text-gray-900 truncate block text-sm" [title]="degree.cancel_reason || '-'">
                        {{ degree.cancel_reason || '-' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      } @else {
        <div class="flex flex-col items-center justify-center p-12 text-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 transition-colors hover:border-gray-300">
          <span class="material-symbols-outlined text-gray-300 text-6xl mb-4">school</span>
          <p class="text-gray-500 font-semibold m-0 text-base leading-relaxed">{{ 'contacts.degrees.no_degrees' | translate }}</p>
        </div>
      }
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DegreesSectionComponent {
  // Input: Degrees data
  readonly degrees = input<ContactDegreeDetail[]>([]);

  /**
   * Enforces system-wide compound title format: {code} - {name}
   */
  getDegreeTitle(degree: ContactDegreeDetail): string {
    if (degree.degree_code && degree.degree_name) {
      return `${degree.degree_code} - ${degree.degree_name}`;
    }
    return degree.degree_name || degree.degree_code || '-';
  }

  isStatusActive(statusName: string | null | undefined): boolean {
    if (!statusName) return false;
    const lower = statusName.toLowerCase();
    return lower.includes('aktiv') || lower.includes('garantiert') || lower.includes('leistung') || lower.includes('bestanden');
  }

  isStatusCancelled(statusName: string | null | undefined): boolean {
    if (!statusName) return false;
    const lower = statusName.toLowerCase();
    return lower.includes('abgebrochen') || lower.includes('storniert') || lower.includes('gestoppt') || lower.includes('abgesagt');
  }

  /**
   * Format date to German locale
   */
  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  }
}
